import React, { useEffect, useMemo, useState } from "react";
import PageLayout from "../../components/layout/PageLayout";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useGetPaymentAccountsQuery } from "../../store/api";
import { extractPagination } from "../../utils/response";
import { toast } from "react-toastify";
import Can from "../../components/permissions/Can";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";
import {
  PAYMENT_ACCOUNTS_EDIT_PATH,
  PAYMENT_ACCOUNTS_VIEW_PATH,
} from "../../navigations/path";
import SearchIcon from "@mui/icons-material/Search";

const PaymentAccounts = () => {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const queryParams = useMemo(
    () => ({
      page: pagination.page,
      per_page: pagination.per_page,
      paginate: 1,
      ...(search ? { search } : {}),
    }),
    [pagination.page, pagination.per_page, search],
  );

  const {
    data,
    isFetching: loading,
    error,
  } = useGetPaymentAccountsQuery(queryParams);

  const rows = useMemo(() => {
    const list =
      data?.data?.list ??
      data?.list ??
      (Array.isArray(data?.data) ? data.data : []);
    return Array.isArray(list) ? list : [];
  }, [data]);

  useEffect(() => {
    if (!data) return;
    const pag = extractPagination(data);
    setPagination((p) => ({ ...p, total: pag.total ?? 0 }));
  }, [data]);

  useEffect(() => {
    if (error) toast.error(error?.data?.message || "Failed to load payments");
  }, [error]);

  const headCellSx = { fontWeight: 600, whiteSpace: "nowrap" };

  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleChangeRowsPerPage = (event) => {
    const per = parseInt(event.target.value, 10) || 10;
    setPagination((prev) => ({ ...prev, page: 1, per_page: per }));
  };

  const handleChangePage = (_, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearch(searchInput.trim());
  };

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography
          variant="h4"
          sx={{ mb: 3, color: "primary.main", fontWeight: 600 }}
        >
          Payments Accounts
        </Typography>
        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder="Search payments..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
          />
          <Button variant="outlined" onClick={handleSearch}>
            Search
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setSearchInput("");
              setSearch("");
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          >
            Clear
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ overflowX: "auto" }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "grey.100" }}>
                    <TableCell sx={headCellSx}>Account Name</TableCell>
                    <TableCell sx={headCellSx}>Descriptor</TableCell>
                    <TableCell sx={headCellSx}>Daily Limit</TableCell>
                    <TableCell sx={headCellSx}>Monthly Limit</TableCell>
                    <TableCell sx={headCellSx}>Max Allowed</TableCell>
                    <TableCell sx={headCellSx}>Status</TableCell>
                    <TableCell sx={headCellSx}>Created At</TableCell>
                    <TableCell
                      sx={{ ...headCellSx, width: 72, textAlign: "center" }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No payments found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => {
                      return (
                        <TableRow key={row.id} hover>
                          <TableCell>{row.name}</TableCell>

                          <TableCell>{row.descriptor || "-"}</TableCell>

                          <TableCell>{row.daily_limit}</TableCell>

                          <TableCell>{row.monthly_limit}</TableCell>

                          <TableCell>{row.max_allowed}</TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={row.status}
                              color={
                                row.status === "active" ? "success" : "error"
                              }
                            />
                          </TableCell>

                          <TableCell>{row.created_at}</TableCell>

                          <TableCell
                            onClick={(e) => e.stopPropagation()}
                            sx={{ textAlign: "center" }}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenMenu(e, row)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && (
              <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={pagination.per_page}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page"
              />
            )}
          </CardContent>
        </Card>
      </Container>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Can permission="payment-account-view">
          <MenuItem
            onClick={() =>
              navigate(
                PAYMENT_ACCOUNTS_VIEW_PATH.replace(":id", selectedRow?.id),
              )
            }
          >
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>
        </Can>
        <Can permission="payment-account-edit">
          <MenuItem
            onClick={() =>
              navigate(
                PAYMENT_ACCOUNTS_EDIT_PATH.replace(":id", selectedRow?.id),
              )
            }
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        </Can>
        <Can permission="payment-account-delete">
          <MenuItem
            onClick={() => console.log("Delete clicked")}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Can>
      </Menu>
    </PageLayout>
  );
};

export default PaymentAccounts;
